import * as React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { CircularProgress, createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';

import { Button, Typography } from 'components/v2';
import { useLumaBotContext } from 'modules/LumaBotV2/LumaBot.context';
import { EMessageType } from 'modules/LumaBotV2/WIP/ui-model/types';
import { usePageMutationCreator } from 'ui-model';
import { useLumaBotGetTemplateText } from 'modules/LumaBotV2/useLumaBotGetTemplateText';

const messages = defineMessages({
  rescheduleAppointment: {
    id: 'components.v2.RescheduleButton.rescheduleAppointment',
    defaultMessage: 'Reschedule',
  },
  retry: {
    id: 'components.v2.RescheduleButton.retry',
    defaultMessage: 'Retry',
  },
});

const styles = ({ spacing }: Theme) =>
  createStyles({
    rescheduleApptBtn: {
      margin: `${spacing.unit}px 0`,
    },
  });

export interface AppointmentItemSMSActionButtonProps extends WithStyles<typeof styles> {
  appointmentId: string;
  disabled: boolean;
}

enum EStatus {
  Initial = 'initial',
  Retry = 'retry',
}

export const usePostLumaBotReschedulerReminder = () =>
  usePageMutationCreator({
    key: 'usePostLumaBotReschedulerReminder',
    url: 'lumabotPage/reminder',
    method: 'POST',
    params: {},
  });

const AppointmentItemSMSActionButton = (props: AppointmentItemSMSActionButtonProps) => {
  const rescheduleText = useLumaBotGetTemplateText('patientLumaBotManageAppointmentReschedule');

  const { pushAnswer } = useLumaBotContext();
  const { appointmentId, disabled, classes } = props;
  const { formatMessage } = useIntl();
  const [status, setStatus] = React.useState(EStatus.Initial);

  const { mutate: postSMSReschedulerReminder, isLoading } = usePostLumaBotReschedulerReminder();

  const handleSubmit = React.useCallback(() => {
    postSMSReschedulerReminder(
      {
        appointment: appointmentId,
      },
      {
        onSettled: () => setStatus(EStatus.Retry),
        onSuccess: () => {
          pushAnswer({
            type: EMessageType.Inbound,
            text: rescheduleText,
          });
        },
      },
    );
  }, [appointmentId, postSMSReschedulerReminder, pushAnswer, rescheduleText]);

  return (
    <Button
      data-lh-id="button-RescheduleButton"
      className={classes.rescheduleApptBtn}
      color="primary"
      variant="contained"
      disabled={isLoading || disabled}
      onClick={handleSubmit}
      fullWidth
      extraRounded
      extraPadding
    >
      {isLoading && <CircularProgress size={21} />}
      {status === EStatus.Initial && !isLoading && (
        <Typography variant="bW600F14" color="inherit" noWrap>
          {rescheduleText}
        </Typography>
      )}
      {status === EStatus.Retry && !isLoading && (
        <Typography variant="bW600F14" color="inherit" noWrap>
          {formatMessage(messages.retry)}
        </Typography>
      )}
    </Button>
  );
};

export default withStyles(styles, { name: 'AppointmentItemSMSActionButton' })(AppointmentItemSMSActionButton);
